import React, { useEffect, useState } from "react";
import { useIntl } from "react-intl";
import { Col, Row } from "reactstrap";
import Toast from "../../helpers/Toast";
import { GetClientAssistanceCreateRequestDTO } from "../../models/Assistance/GetClientAssistanceCreateRequestDTO";
import { SearchByCarPlate } from "../../models/Equipment/SearchByCarPlate";
import { SearchByNSerie } from "../../models/Equipment/SearchByNSerie";
import { ClientService } from "../../services/ClientService";
import { EquipmentService } from "../../services/EquipmentService";
import InputComponent from "../InputComponent";
import { useMobile, useTablet } from "../Layout/UseResponsive";
import SearchSelectComponent from "../SearchSelectComponent";
import SoftwareNameAndVersionComponent from "../SoftwareNameAndVersionComponent";
import { AssistanceDTO } from "../../models/Assistance/AssistanceDTO";
import { useAvailableModulesContext } from "../../context/AvailableModulesContext";
import UpdateClientComponent from "../Assistances/UpdateClientComponent";
import SwitchCheckListComponent from "../Assistances/DynamicComponents/SwitchCheckListComponent";

export type SelectItemsProps = {
  label: string;
  value: string;
  data?: any;
};

interface ClientEquipmentBidirecionalProps {
  isActiveProjectModule: boolean;
  projectSelected: any;
  clientSelected: SelectItemsProps | null;
  setClientSelected: (client: SelectItemsProps | null) => void;
  serialNumberSelected: SelectItemsProps | null;
  setSerialNumberSelected: (serial: SelectItemsProps | null) => void;
  equipment: any;
  setEquipment: (equip: any) => void;
  isSearchEquipmentLoading: boolean;
  setIsSearchEquipmentLoading: (loading: boolean) => void;

  clientErpId?: string | null; // Prop para obter / editar dados
  equipmentErpId?: string | null; // Prop para obter / editar dados

  isDisabled?: boolean;

  isActiveSearchByCarPlate: boolean;
  carPlateSelected: SelectItemsProps | null;
  setCarPlateSelected: (serial: SelectItemsProps | null) => void;

  assistance?: AssistanceDTO;
  setAssistance?: Function | undefined;

  disableSoftwareFields?: boolean;

  // Propriedades do UpdateClientComponent
  isActiveChangeClientInEquipmentAndAssistance?: boolean;
  isActiveErpEquipments?: boolean;
  assistanceHasExecutions?: boolean;
  assistanceStatus?: string;
  isValidAssistanceStatusToChangeClientAndEquipment?: (status: string) => boolean;
  assistanceId?: number | null;

}

const ClientEquipmentBidirecional: React.FC<ClientEquipmentBidirecionalProps> = ({
  isActiveProjectModule,
  projectSelected,
  clientSelected,
  setClientSelected,
  serialNumberSelected,
  setSerialNumberSelected,
  equipment,
  setEquipment,
  isSearchEquipmentLoading,
  setIsSearchEquipmentLoading,

  clientErpId,
  equipmentErpId,

  isDisabled,

  isActiveSearchByCarPlate,
  carPlateSelected,
  setCarPlateSelected,

  assistance,
  setAssistance,

  disableSoftwareFields,

  // Novas propriedades do UpdateClientComponent
  isActiveChangeClientInEquipmentAndAssistance,
  isActiveErpEquipments,
  assistanceHasExecutions,
  assistanceStatus,
  isValidAssistanceStatusToChangeClientAndEquipment,
  assistanceId,

}) => {


  const equipmentService = new EquipmentService();
  const clientService = new ClientService();

  const isMobile = useMobile();
  const isTablet = useTablet();

  const intl = useIntl();
  const { availableModules } = useAvailableModulesContext();
  const [isActiveSoftwareModule, setIsActiveSoftwareModule] = useState<boolean>(false);
  const [isActiveWorkshopInternalNumber, setIsActiveWorkshopInternalNumber] = useState<boolean>(false);


  useEffect(() => {
    if (availableModules.includes("IsActiveSoftwareModule")) {
      setIsActiveSoftwareModule(true);
    }
    if (availableModules.includes("IsActiveWorkshopInternalNumber")) {
      setIsActiveWorkshopInternalNumber(true);
    }
  }, [availableModules]);

  const GetByClient = async (value: string): Promise<SelectItemsProps[]> => {
    var data: GetClientAssistanceCreateRequestDTO = {
      clientSearch: value,
    };

    var response = await clientService.GetClientAssistanceCreate(data);
    if (response.success === false || response.obj === null) {
      Toast.Show("error", response.message);
      return [];
    }

    return response.obj.map((element: any) => ({
      value: element.clientErpId,
      label: element.clientName,
    }));
  };

  const GetEquipmentsFromSelectedClient = async (labelType: 'serialNumber' | 'carPlate'): Promise<SelectItemsProps[]> => {
    if (!clientSelected) return [];

    const data: GetClientAssistanceCreateRequestDTO = { clientSearch: clientSelected.value };
    const response = await clientService.GetEquipmentsFromClient(data);

    if (!response.success || !response.obj) {
      Toast.Show("error", response.message);
      return [];
    }

    return response.obj.map((element: any) => ({
      value: element.equipmentErpId,
      label: labelType === 'serialNumber' ? element.equipmentNumSerie : element.equipmentCarPlate,
    }));
  };

  const GetBySerialNumber = async (value: string): Promise<SelectItemsProps[]> => {
    var data: SearchByNSerie = { serialNumber: value };
    var response = await equipmentService.GetByNSerie(data);

    if (!response.success || !response.obj) {
      Toast.Show("error", response.message);
      return [];
    }

    return response.obj.map((element: any) => ({
      value: element.erpId,
      label: element.serialNumber,
    }));
  };

  const handleClientChange = (clientInput: any) => {
    if (isActiveProjectModule && projectSelected) {
      setSerialNumberSelected(null);
      setCarPlateSelected && setCarPlateSelected(null);
      return;
    }

    setClientSelected(clientInput || null);
    setSerialNumberSelected(null);
    setCarPlateSelected && setCarPlateSelected(null);
  };

  //numero de série
  const handleSerialNumberChange = async (serialNumberInput: any) => {
    if (!serialNumberInput) {
      setSerialNumberSelected(null);
      setCarPlateSelected && setCarPlateSelected(null);
      return;
    }

    var data: SearchByNSerie = {
      serialNumber: serialNumberInput.label,
      erpId: serialNumberInput.value,
    };

    const response = await equipmentService.GetByNSerie(data);
    if (!response.success || !response.obj) {
      Toast.Show("error", response.message);
      setIsSearchEquipmentLoading(false);
      return [];
    }

    const equipmentData = response.obj[0];

    setEquipment(response.obj[0]);
    setSerialNumberSelected({
      value: response.obj[0].erpId,
      label: response.obj[0].serialNumber,
    });

    if (!equipmentData.client || !equipmentData.client.erpId) {
      Toast.Show("error", "Este equipamento não tem nenhum cliente associado.");
      setClientSelected(null);
    } else {
      setClientSelected({
        value: response.obj[0].client.erpId,
        label: response.obj[0].client.name,
      });
    }

    if (isActiveSearchByCarPlate) {
      setCarPlateSelected({
        value: response.obj[0].erpId,
        label: response.obj[0].carPlate,
      });
    }
  };

  // matricula
  const handleCarPlateChange = async (carPlateInput: any) => {
    if (!carPlateInput) {
      setCarPlateSelected && setCarPlateSelected(null);
      return;
    }

    const data: SearchByCarPlate = {
      carPlate: carPlateInput.label,
      erpId: carPlateInput.value,
    };

    const response = await equipmentService.GetByCarPlate(data);
    if (!response.success || !response.obj) {
      Toast.Show("error", response.message);
      setIsSearchEquipmentLoading(false);
      return [];
    }

    const equipmentData = response.obj[0];

    setEquipment(response.obj[0]);
    if (isActiveSearchByCarPlate) {
      setCarPlateSelected({
        value: response.obj[0].erpId,
        label: response.obj[0].carPlate,
      });
    }
    setSerialNumberSelected({
      value: response.obj[0].erpId,
      label: response.obj[0].serialNumber,
    });

    if (!equipmentData.client || !equipmentData.client.erpId) {
      Toast.Show("error", "Este equipamento não tem nenhum cliente associado.");
      setClientSelected(null);
    } else {
      setClientSelected({
        value: response.obj[0].client.erpId,
        label: response.obj[0].client.name,
      });
    }
  };

  const getClientSearchSelectValue = () => {
    if (projectSelected !== null) {
      return { value: projectSelected.client.erpId, label: projectSelected.client.name };
    }
    return clientSelected;
  };


  // CHAMAR COMPONENTE PROCESSO DE OBTER / EDIÇÃO DADOS
  useEffect(() => {
    const fetchClientAndEquipment = async () => {
      if (clientErpId) {
        const clientResponse = await clientService.GetByIdErp(clientErpId);
        if (clientResponse.success && clientResponse.obj) {
          setClientSelected({
            value: clientResponse.obj.erpId,
            label: clientResponse.obj.name,
          });
        }
      }

      if (equipmentErpId) {
        const equipmentResponse = await equipmentService.GetByIdErp(equipmentErpId);

        if (equipmentResponse.success && equipmentResponse.obj) {
          setEquipment(equipmentResponse.obj);
          setSerialNumberSelected({
            value: equipmentResponse.obj.erpId,
            label: equipmentResponse.obj.serialNumber,
          });
          if (isActiveSearchByCarPlate) {
            setCarPlateSelected({
              value: equipmentResponse.obj.erpId,
              label: equipmentResponse.obj.carPlate,
            });
          }
        }
      }
    };

    fetchClientAndEquipment();
  }, [clientErpId, equipmentErpId]);

  const GetByCarPlate = async (value: string): Promise<SelectItemsProps[]> => {
    if (value.length <= 3) {
      return [];
    }

    const data: SearchByCarPlate = { carPlate: value };
    const response = await equipmentService.GetByCarPlate(data);

    if (response.success === false || response.obj === null) {
      Toast.Show("error", response.message);
      return [];
    }

    return response.obj.map((element) => ({
      value: element.erpId,
      label: element.carPlate,
    }));
  };

  return (
    <>
      {/* TODO */}
      {/* LAYOUT PODE SER MELHORADO, PASSANDO PROPS PARA COMPONENTES / USANDO USE RESPONSIVE COMPONENT */}

      {/* Seção de seleção de cliente, dependente do estado do módulo obras */}
      <Row style={{ margin: "0px", padding: "0px", alignItems: "flex-end", alignContent: "flex-end" }}>
        {isActiveProjectModule ? (
          <InputComponent
            xl={9}
            lg={9}
            md={9}
            sm={12}
            xs={12}
            style={{ width: "100%" }}
            type={"text"}
            value={
              clientSelected ? clientSelected.label : ""
            }
            label={intl.formatMessage({ id: "ClientEquipmentBidirecional.Client" })}
            readOnly={true}
            showLoadingSkeleton={isSearchEquipmentLoading}
          />
        ) : (
          <>
            <Col
              xl={9}
              lg={9}
              md={9}
              sm={12}
              xs={12}>
              <SearchSelectComponent
                label={intl.formatMessage({ id: "ClientEquipmentBidirecional.Client" })}
                loadOptions={GetByClient}
                onChange={(e) => handleClientChange(e)}
                value={getClientSearchSelectValue()}
                style={{ width: "100%" }}
                isDisabled={isDisabled}
              />
            </Col>
            <Col xl={3} lg={3} md={3} sm={12} xs={12} style={{ paddingLeft: isMobile || isTablet ? "10px" : "0px" }}>
              {/* UpdateClientComponent */}
              {isActiveErpEquipments === false && isActiveChangeClientInEquipmentAndAssistance === true &&
                isValidAssistanceStatusToChangeClientAndEquipment && assistanceStatus &&
                isValidAssistanceStatusToChangeClientAndEquipment(assistanceStatus) &&
                assistanceHasExecutions === false && (
                  <UpdateClientComponent
                    equipmentBrand={equipment?.brand ?? null}
                    equipmentErpId={equipment?.erpId ?? null}
                    equipmentModel={equipment?.model ?? null}
                    equipmentName={equipment?.name ?? null}
                    equipmentCarPlate={equipment?.carPlate ?? null}
                    equipmentSerialNumber={equipment?.serialNumber ?? null}
                    loading={isSearchEquipmentLoading}
                    clientSelected={clientSelected}
                    setClientSelected={setClientSelected}
                    assistanceId={assistanceId ?? null}
                    isActiveSearchByCarPlate={isActiveSearchByCarPlate}
                  />
                )
              }
            </Col>
          </>
        )}

      </Row>

      {/* Seção de seleção de equipamento */}
      {isActiveSearchByCarPlate && (
        <Row style={{ margin: "0px", padding: "0px", alignItems: "flex-end", alignContent: "flex-end" }}>
          <SearchSelectComponent
            xl={4}
            lg={4}
            md={4}
            sm={12}
            xs={12}
            label={intl.formatMessage({ id: "ClientEquipmentBidirecional.CarPlate" })}
            loadOptions={clientSelected ? () => GetEquipmentsFromSelectedClient('carPlate') : GetByCarPlate}
            onChange={handleCarPlateChange}
            value={carPlateSelected}
            style={{ width: "100%" }}
            isDisabled={isDisabled}
          />

          {isActiveWorkshopInternalNumber && (
            <InputComponent
              xl={2}
              lg={2}
              md={2}
              sm={12}
              xs={12}
              type={"text"}
              label={intl.formatMessage({ id: "ClientEquipmentBidirecional.AssistanceExtraNumber" })}
              value={assistance?.workshopInternalNumber}
              style={{ width: "100%" }}
              onChange={(workshopInternalNumber: string) => {
                setAssistance?.({ ...assistance, workshopInternalNumber });
              }}
              showLoadingSkeleton={isSearchEquipmentLoading}
            />
          )}
        </Row>
      )}

      <Row style={{ margin: "0px", padding: "0px", alignItems: "flex-end", alignContent: "flex-end" }}>
        <SearchSelectComponent
          xl={4}
          lg={4}
          md={4}
          sm={12}
          xs={12}
          label={intl.formatMessage({ id: "ClientEquipmentBidirecional.SerialNumber" })}
          loadOptions={clientSelected ? () => GetEquipmentsFromSelectedClient('serialNumber') : GetBySerialNumber}
          onChange={handleSerialNumberChange}
          value={serialNumberSelected}
          style={{ width: "100%" }}
          isDisabled={isDisabled}
        />
        <InputComponent
          xl={4}
          lg={4}
          md={4}
          sm={12}
          xs={12}
          style={{ width: "100%" }}
          type={"text"}
          value={serialNumberSelected ? equipment?.brand : ""}
          label={intl.formatMessage({ id: "ClientEquipmentBidirecional.Brand" })}
          readOnly={true}
          showLoadingSkeleton={isSearchEquipmentLoading}
        />
        <InputComponent
          xl={4}
          lg={4}
          md={4}
          sm={12}
          xs={12}
          style={{ width: "100%" }}
          type={"text"}
          value={serialNumberSelected ? equipment?.model : ""}
          label={intl.formatMessage({ id: "ClientEquipmentBidirecional.Model" })}
          readOnly={true}
          showLoadingSkeleton={isSearchEquipmentLoading}
        />
      </Row>
      {(isActiveSoftwareModule && !disableSoftwareFields && equipment?.id) && (
        <Row style={{ margin: "0px", padding: "0px", alignItems: "flex-end", alignContent: "flex-end" }}>
          <InputComponent
            xl={4}
            lg={4}
            md={4}
            sm={12}
            xs={12}
            style={{ width: "100%" }}
            type={"text"}
            value={assistance?.softwareName ?? ""}
            label={intl.formatMessage({ id: "ClientEquipmentBidirecional.SoftwareName" })}
            readOnly={true}
            showLoadingSkeleton={isSearchEquipmentLoading}
          />
          <InputComponent
            xl={4}
            lg={4}
            md={4}
            sm={12}
            xs={12}
            style={{ width: "100%" }}
            type={"text"}
            value={assistance?.softwareVersion ?? ""}
            label={intl.formatMessage({ id: "ClientEquipmentBidirecional.SoftwareVersion" })}
            readOnly={true}
            showLoadingSkeleton={isSearchEquipmentLoading}
          />

          <SoftwareNameAndVersionComponent
            equipmentId={equipment.id}
            currentSoftwareName={assistance?.softwareName ?? null}
            currentSoftwareVersion={assistance?.softwareVersion ?? null}
            assistance={assistance}
            loading={isSearchEquipmentLoading}
            setAssistance={setAssistance}
            userCanEdit={!assistance?.userCanEdit}
          ></SoftwareNameAndVersionComponent>
        </Row>
      )}

      <Row style={{ margin: "0px", padding: "0px", alignItems: "flex-end", alignContent: "flex-end" }}>
        <InputComponent
          xl={4}
          lg={4}
          md={4}
          sm={12}
          xs={12}
          style={{ width: "100%" }}
          type={"text"}
          value={serialNumberSelected ? equipment?.sku : ""}
          label={intl.formatMessage({ id: "ClientEquipmentBidirecional.Sku" })}
          readOnly={true}
          showLoadingSkeleton={isSearchEquipmentLoading}
        />
      </Row>
    </>
  );
};

export default ClientEquipmentBidirecional;
